import { isMobileOnly } from 'mobile-device-detect';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Tab } from 'semantic-ui-react';
import uniqid from 'uniqid';
import { ReservationPolicy } from '../../api/services/next';
import { GetResourcesByList } from '../../api/services/shell';
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { useMessageResources, useTextResources } from '../../language';
import { Languages } from '../shared/constants';
import { onError, onSuccess, onWarning } from '../shared/messages';
import { buildAutoDescription } from '../shared/reservationPolicy';
import { useLoader } from "../../hooks/useLoader";
import { RequiredGtdTypeEnum } from '../shared/constants';
import { convertUtcTimeToHotelLocal, convertLocalTimeToUtc } from '../shared/timezoneUtils';
import { propertiesEnum, sourceEnum } from './enums';
import GuaranteePolicyPane from './GuaranteePolicyPane';
import ReusablePane from './reusablePane';
import './style.css';

const CancellationAndDepositPoliciesPopup = ({
  reservationPolicy,
  onClose,
  startOpen,
  ...rest
}) => {
  const text = useTextResources();
  const message = useMessageResources();
  const loader = useLoader();
  const currentHotel = useCurrentHotel();

  const [open, setOpen] = useState(startOpen);
  // HS-52867 fix: Use deep clone to completely detach from shared references
  const [depositsCopy, setDepositsCopy] = useState(
    JSON.parse(JSON.stringify(reservationPolicy.DepositPolicies || []))
  );
  const [cancellationsCopy, setCancellationsCopy] = useState(
    JSON.parse(JSON.stringify(reservationPolicy.CancellationPolicies || []))
  );
  const [timeReference, setTimeReference] = useState(() => {
    // First check TimeReference on ReservationPolicy, then fall back to first policy's TimeReference
    const firstPolicy = reservationPolicy.CancellationPolicies?.[0] || reservationPolicy.DepositPolicies?.[0];
    return reservationPolicy.TimeReference ?? firstPolicy?.TimeReference ?? null;
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [depositsErrors, setDepositsErrors] = useState({});
  const [cancellationsErrors, setCancellationsErrors] = useState({});

  const [deletedDeposits, setDeletedDeposits] = useState([])
  const [deletedCancellations, setDeletedCancellations] = useState([])

  // GTD (Guarantee Policy) state — single config on the reservation policy, not an array
  const [requiredGtdType, setRequiredGtdType] = useState(reservationPolicy.RequiredGtdType ?? RequiredGtdTypeEnum.NoGuaranteeRequired);
  // guaranteedTime is stored as hotel-local time for display/editing.
  // Server sends/receives GtdUtcTime in UTC; we convert UTC→local on init, local→UTC on save.
  const [guaranteedTime, setGuaranteedTime] = useState(() => {
    const utcTime = reservationPolicy.GtdUtcTime;
    if (!utcTime) return '';
    return convertUtcTimeToHotelLocal(utcTime, currentHotel?.HotelParameter?.TimeZone) || utcTime.substring(0, 5);
  });

  useEffect(() => {
    setOpen(startOpen);
  }, [startOpen]);

  const close = (props) => {
    setOpen(false);
    onClose?.(props);
    clearScreenData();
  };

  const clearScreenData = () => {
    setHasChanges(false);
    resetErrors();
    setDepositsCopy([]);
    setCancellationsCopy([]);
  };

  const resetErrors = () => {
    setCancellationsErrors({});
    setDepositsErrors({});
  };

  // --- GTD handlers ---
  const onGtdTypeChange = useCallback((value) => {
    setRequiredGtdType(value);
    setHasChanges(true);
    // When switching to "No Guarantee Required", clear the time
    if (value === RequiredGtdTypeEnum.NoGuaranteeRequired) {
      setGuaranteedTime('');
    }
  }, []);

  const onGuaranteedTimeChange = useCallback((value) => {
    setGuaranteedTime(value);
    setHasChanges(true);
  }, []);

  const save = async () => {
    try {
      if (!hasChanges || !checkIsValidForm()) return;

      loader(true);

      // Build the reservation policy with GTD fields merged in.
      // guaranteedTime is hotel-local; convert back to UTC for the server.
      const gtdUtcTime = guaranteedTime
        ? convertLocalTimeToUtc(guaranteedTime, currentHotel?.HotelParameter?.TimeZone) || guaranteedTime
        : null;

      const policyToSave = {
        ...reservationPolicy,
        TimeReference: timeReference,
        RequiredGtdType: requiredGtdType,
        // Send as HH:mm:ss for backend TimeSpan/TimeOnly parsing
        GtdUtcTime: gtdUtcTime ? `${gtdUtcTime}:00` : null,
      };

      const hotelUtcOffset = currentHotel?.UtcOffset ?? null;

      const autoDescriptionKeys = {
        messages: [
          'BaseCancellationPolicyAutomaticDescription',
          'BaseDepositPolicyAutomaticDescription',
          'LowestCostPolicyAutomaticDescription',
          'PercentPolicyAutomaticDescription',
          'AfterCreatedReservation',
          'GuaranteeDescRequires',
          'GuaranteeDescValidUntil',
        ],
        texts: [
          'BeforeArrivalDate',
          'GuaranteeDescCcGuaranteed',
          'GuaranteeDescVoucherGuaranteed',
          'GuaranteeDescOnArrivalDate',
        ],
      };

      const [hebrewResources, englishResources] = await Promise.all([
        GetResourcesByList(Languages.Hebrew.ID, autoDescriptionKeys.messages, autoDescriptionKeys.texts),
        GetResourcesByList(Languages.English.ID, autoDescriptionKeys.messages, autoDescriptionKeys.texts),
      ]);

      const gtdInfo = requiredGtdType != null ? {
        requiredGtdType,
        gtdUtcTime: gtdUtcTime ? `${gtdUtcTime}:00` : null,
        hotelTimezone: currentHotel?.HotelParameter?.TimeZone,
      } : null;

      const buildDesc = (resources) => {
        const msg = (key) => resources?.messages?.[key] ?? `[${key}]`;
        const txt = (key) => resources?.texts?.[key] ?? `[${key}]`;
        return buildAutoDescription(cancellationsCopy, depositsCopy, msg, txt, gtdInfo);
      };

      const automaticDescriptions = {
        [Languages.Hebrew.ID]: buildDesc(hebrewResources),
        [Languages.English.ID]: buildDesc(englishResources),
      };

      const newReservationPolicy = await ReservationPolicy.SaveReservationPolicy({
        Spec: {
          ReservationPolicy: policyToSave,
          CancellationPolicies: cancellationsCopy,
          DepositPolicies: depositsCopy,
          DeletedCancellationPolicies: deletedCancellations,
          DeletedDepositPolicies: deletedDeposits,
          HotelUtcOffset: hotelUtcOffset,
        },
        AutomaticDescriptions: automaticDescriptions,
      });

      onSuccess(message('SaveReservationPolicyContentUpdate'));
      loader(false);
      close({
        oldReservationPolicyId: reservationPolicy.Id,
        reservationPolicy: newReservationPolicy,
      });
    } catch (e) {
      onError(e);
      console.error(e);
    } finally {
      loader(false);
    }
  };

  const onTimeReferenceChange = useCallback(
    ({ value }) => {
      setHasChanges(true);
      setTimeReference(value);
      // HS-52867 fix: Only update TimeReference on NEW policies (Id === 0), not existing ones
      // Existing policies should keep their original TimeReference
      setDepositsCopy(prev => (prev || []).filter(p => p).map(p => 
        p.Id === 0 ? { ...p, TimeReference: value, IsDirty: true } : p
      ));
      setCancellationsCopy(prev => (prev || []).filter(p => p).map(p => 
        p.Id === 0 ? { ...p, TimeReference: value, IsDirty: true } : p
      ));
    },
    []
  );

  const onUpdatePolicy = useCallback(
    ({ value, policy, policiesArray, property, setStateFunction }) => {
      policy[property] = value;
      policy.IsDirty = true;

      setStateFunction([...policiesArray]);
      setHasChanges(true);
      resetErrors();
    },
    []
  );

  const validate = useCallback(() => {
    if (timeReference == null) {
      return false;
    }
    return true;
  }, [timeReference])

  const onAddNewPolicy = useCallback(
    (setStateFunction, source) => {
      if (!validate()) {
        return;
      }

      const newPolicy = {
        Id: 0,
        InternalId: uniqid('newPolicy-'),
        TimeReference: timeReference,
        ReservationPolicyId: reservationPolicy.Id,
        Days: 0,
        ...(source === sourceEnum.cancellations ? { CancellationTime: '00:00' } : {}),
        Percent: null,
        Amount: null,
        CurrencyId: currentHotel?.MainCurrencyId,
        IsDirty: true,
      };

      setStateFunction((prev) => {
        if (prev) {
           return [newPolicy, ...prev];
        }
        return [newPolicy]
      });
      setHasChanges(true);
    },
    [reservationPolicy.Id, timeReference, currentHotel]
  );

  const onDeletePolicy = useCallback(
    async ({ policy, setStateFunction, setDeletedFunction }) => {
      setHasChanges(true);
      setStateFunction((prev) =>
        prev.filter(
          (p) => p.InternalId != policy.InternalId || p.Id != policy.Id
        )
      );
      if (policy.Id) {
        setDeletedFunction((prev) => [...prev, policy.Id])
      }
    },
    [depositsCopy]
  );


  const currencyValid = useCallback((policy) => {
    if (!policy) return true;
    if (!policy.ApplyLowestCostOption && !policy.Percent && !policy.CurrencyId && policy.Amount) return false;
    return true;
  }, []);

  const amountValid = useCallback((policy) => {
    if (!policy) return true;
    if (!policy.ApplyLowestCostOption && !policy.Percent && !policy.Amount && policy.CurrencyId) return false;
    return true;
  }, []);

  const depositsEitherAmountOrPercentsValid = useCallback((policy) => {
    if (!policy) return true;
    if (!policy.ApplyLowestCostOption && policy.Amount && policy.Percent) return false;
    return true;
  }, []);

  const applyLowestCostOptionValid = useCallback((policy) => {
    if (!policy) return true;
    if (policy.ApplyLowestCostOption && (!policy.Amount || policy.Amount === 0) && (!policy.Percent || policy.Percent === 0)) return false;
    return true;
  }, []);

  const daysFieldValid = useCallback((policy) => {
    return policy.Days != null && policy.Days >= 0;
  }, []);

  const cancellationTimeValid = useCallback((policy, source) => {
    // CancellationTime is required only for cancellations when Days is 0
    if (source === sourceEnum.cancellations && policy.Days === 0) {
      return policy.CancellationTime != null && policy.CancellationTime !== '';
    }
    return true;
  }, []);

  const hasDuplicateCancellationTimes = useCallback((policies) => {
    if (!policies || policies.length < 2) return false;
    
    const validPolicies = policies.filter(p => p && p.Days != null);
    const uniquePolicies = new Set();
    
    const normalizeTime = (time) => {
      if (!time) return '';
      const parts = time.split(':');
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
    };
    
    for (const policy of validPolicies) {
      const key = `${policy.TimeReference}-${policy.Days}-${normalizeTime(policy.CancellationTime)}`;
      if (uniquePolicies.has(key)) {
        return true;
      }
      uniquePolicies.add(key);
    }
    return false;
  }, []);

  const hasDuplicateDepositDays = useCallback((policies) => {
    if (!policies || policies.length < 2) return false;

    const validPolicies = policies.filter(p => p && p.Days != null);
    const uniquePolicies = new Set();

    for (const policy of validPolicies) {
      const key = `${policy.TimeReference}-${policy.Days}`;
      if (uniquePolicies.has(key)) {
        return true;
      }
      uniquePolicies.add(key);
    }
    return false;
  }, []);

  const setErrorAndMarkShouldNotSave = (policy, setErrors, source) => {
    if (!policy) return false;
    let hasError = false;
    const errorSetter = ({ property, text }) => {
      setErrors((prev) => ({
        ...(prev || {}),
        [property]: {
          ...((prev || {})[property] || {}),
          [policy.Id || policy.InternalId]: text,
        },
      }));
    };

    if (!currencyValid(policy)) {
      errorSetter({
        text: text('CurrencyMustAccompanyAmount'),
        property: propertiesEnum.CurrencyId,
      });
      hasError = true;
    }

    if (!amountValid(policy)) {
      errorSetter({
        text: text('AmountMustAccompanyCurrency'),
        property: propertiesEnum.Amount,
      });
      hasError = true;
    }

    if (!daysFieldValid(policy)) {
      errorSetter({
        text: text('PleaseChooseNumberOfDays'),
        property: propertiesEnum.Days,
      });
      hasError = true;
    }

    if (!depositsEitherAmountOrPercentsValid(policy)) {

      switch (source) {
        case sourceEnum.deposits:
          onWarning(message('DepositPolicyOnlyOneCostOption'))
          break;
        case sourceEnum.cancellations:
          onWarning(message('CancellationPolicyOnlyOneCostOption'))
          break;
        default:
          break;
      }

      errorSetter({
        text: text('EitherAmountOrPercents'),
        property: propertiesEnum.AmountOrPercentInDeposits,
      });
      hasError = true;
    }

    if (!applyLowestCostOptionValid(policy)) {
      onWarning(message('MustfillInAtLeastOneValueOfAPercentageAmount'));
      errorSetter({
        text: text('EitherAmountOrPercents'),
        property: propertiesEnum.AmountOrPercentInDeposits,
      });
      hasError = true;
    }

    if (!cancellationTimeValid(policy, source)) {
      errorSetter({
        text: message('RequiredField').replace("{0}", text('CancellationTime')).replace(":", ""),
        property: propertiesEnum.CancellationTime,
      });
      hasError = true;
    }

    return hasError;
  };

  const checkIsValidForm = () => {
    let shouldSave = true;

    // GTD validation: if guarantee is required, time must be provided
    if (requiredGtdType !== RequiredGtdTypeEnum.NoGuaranteeRequired && !guaranteedTime) {
      onWarning(message('RequiredField').replace("{0}", text('GuaranteedTime')).replace(":", ""));
      shouldSave = false;
      return shouldSave;
    }

    for (const deposit of (depositsCopy || []).filter(p => p)) {
      const hasError = setErrorAndMarkShouldNotSave(deposit, setDepositsErrors, sourceEnum.deposits)
      if (hasError) {
        shouldSave = false;
        break;
      }
    }

    for (const cancellation of (cancellationsCopy || []).filter(p => p)) {
      const hasError = setErrorAndMarkShouldNotSave(cancellation, setCancellationsErrors, sourceEnum.cancellations)
      if (hasError) {
        shouldSave = false;
        break;
      }
    }

    if (shouldSave && hasDuplicateCancellationTimes(cancellationsCopy)) {
      onWarning(message('DuplicateCancellationDaysAndTimesNotAllowed'));
      shouldSave = false;
    }

    if (shouldSave && hasDuplicateDepositDays(depositsCopy)) {
      onWarning(message('DuplicateDepositDaysNotAllowed'));
      shouldSave = false;
    }

    return shouldSave;
  };

  const valuesAndHandlersPackage = useMemo(
    () => ({
      timeReference,
      onTimeReferenceChange,
      onUpdatePolicy,
      onDeletePolicy,
      onAddNewPolicy,
    }),
    [
      onTimeReferenceChange,
      onAddNewPolicy,
      onDeletePolicy,
      onUpdatePolicy,
      timeReference,
    ]
  );

  const depositsPane = useCallback(
    () => (
      <ReusablePane
        key='depositsPane'
        policiesArray={depositsCopy}
        setStateFunction={setDepositsCopy}
        setDeletedFunction={setDeletedDeposits}
        source={sourceEnum.deposits}
        errors={depositsErrors}

        {...valuesAndHandlersPackage}
      />
    ),
    [depositsCopy, depositsErrors, valuesAndHandlersPackage]
  );

  const cancellationsPane = useCallback(
    () => (
      <ReusablePane
        key='cancellationsPane'
        policiesArray={cancellationsCopy}
        setStateFunction={setCancellationsCopy}
        setDeletedFunction={setDeletedCancellations}
        source={sourceEnum.cancellations}
        errors={cancellationsErrors}
        {...valuesAndHandlersPackage}
      />
    ),
    [cancellationsCopy, cancellationsErrors, valuesAndHandlersPackage]
  );

  const guaranteePane = useCallback(
    () => (
      <GuaranteePolicyPane
        key='guaranteePane'
        requiredGtdType={requiredGtdType}
        guaranteedTime={guaranteedTime}
        onGtdTypeChange={onGtdTypeChange}
        onGuaranteedTimeChange={onGuaranteedTimeChange}
        hotelEta={currentHotel?.Eta}
      />
    ),
    [requiredGtdType, guaranteedTime, onGtdTypeChange, onGuaranteedTimeChange]
  );

  const panes = useMemo(
    () => [
      {
        menuItem: text('CancellationPolicy'),
        render: cancellationsPane,
      },
      {
        menuItem: text('DepositPolicy'),
        render: depositsPane,
      },
      {
        menuItem: text('GuaranteePolicy'),
        render: guaranteePane,
      },
    ],
    [cancellationsPane, depositsPane, guaranteePane]
  );

  return (
      <Modal
        size={isMobileOnly ? 'fullscreen' : 'small'}
        open={open}
        className={isMobileOnly ? 'mobile' : ''}
        style={{ zIndex: 1000000, minHeight: 700, maxHeight: '90vh' }}
        {...rest}
      >
        {!isMobileOnly && (
          <Modal.Header>{text('ReservationPolicy')} - {reservationPolicy?.Name}</Modal.Header>
        )}
        <Modal.Content
          style={{ minHeight: 650, maxHeight: 650, overflowY: 'auto' }} 
        >
          <Tab
          //   renderActiveOnly={false}
            menu={{ secondary: true, pointing: true }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            panes={panes}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button size={'tiny'} negative onClick={() => close()}>
            {text('Close')}
          </Button>
          <Button size={'tiny'} positive onClick={save} disabled={!hasChanges}>
            {text('Save')}
          </Button>
        </Modal.Actions>
      </Modal>
  );
};

CancellationAndDepositPoliciesPopup.propTypes = {
  onClose: PropTypes.func,
  startOpen: PropTypes.bool,
  reservationPolicy: PropTypes.object,
  policiesArray: PropTypes.array,
  setStateFunction: PropTypes.func,
};

export default CancellationAndDepositPoliciesPopup;
